import React, { LegacyRef } from 'react';
import cx from 'classnames';
import {
  Backdrop,
  ClickAwayListener,
  Paper,
  styled,
  Popper as MuiPopper,
  Box,
  PopperPlacementType,
} from '@mui/material';
import { boxShadow } from 'new/constants/styles';
import { TENANT } from 'new/tenants';

const StyledPopper = styled(MuiPopper)(({ theme }) => {
  return {
    zIndex: 3,

    '&[data-popper-placement*="bottom-start"] .arrow, &[data-popper-placement*="bottom-end"] .arrow':
      {
        top: 0,
        left: 0,
        marginTop: '-0.9em',
        width: '3em',
        height: '1em',
        '&::before': {
          borderWidth: '0 1em 1em 1em',
          borderColor: `transparent transparent ${theme.palette.background.paper} transparent`,
        },
      },
    '&[data-popper-placement*="top-start"] .arrow, &[data-popper-placement*="top-end"] .arrow':
      {
        bottom: 0,
        left: 0,
        marginBottom: '-0.9em',
        width: '3em',
        height: '1em',
        '&::before': {
          borderWidth: '1em 1em 0 1em',
          borderColor: `${theme.palette.background.paper} transparent transparent transparent`,
        },
      },
    '&[data-popper-placement*="right"] .arrow': {
      left: 0,
      marginLeft: '-0.9em',
      height: '3em',
      width: '1em',
      '&::before': {
        borderWidth: '1em 1em 1em 0',
        borderColor: `transparent ${theme.palette.background.paper} transparent transparent`,
      },
    },
    '&[data-popper-placement*="left"] .arrow': {
      right: 0,
      marginRight: '-0.9em',
      height: '3em',
      width: '1em',
      '&::before': {
        borderWidth: '1em 0 1em 1em',
        borderColor: `transparent transparent transparent ${theme.palette.background.paper}`,
      },
    },
    '& .MuiPaper-root': {
      boxShadow,
      borderRadius: 5,
      fontFamily: TENANT.theme.modal.fontFamily,
      fontSize: '1rem',
      overflow: 'hidden',
    },
    '.arrow': {
      position: 'absolute',
      fontSize: 7,
      width: '3em',
      height: '3em',
      '&::before': {
        content: '""',
        margin: 'auto',
        display: 'block',
        width: 0,
        height: 0,
        borderStyle: 'solid',
      },
    },
  };
});

type ChildrenProps = {
  id?: string;
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
};

type RenderChildrenFn = (onClose: () => void) => React.ReactNode;

type Props = {
  id?: string;
  children: (props: ChildrenProps) => React.ReactNode;
  popperContent: React.ReactNode | RenderChildrenFn;
  placement: PopperPlacementType;
  hasBackdrop?: boolean;
  isAdditionalMarginTop?: boolean;
  isCombinedPieChart?: boolean;
};

const PopperMenu: React.FC<Props> = ({
  id,
  children,
  popperContent,
  placement,
  hasBackdrop = true,
  isAdditionalMarginTop,
  isCombinedPieChart,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [arrowRef, setArrowRef] = React.useState<HTMLSpanElement | null>(null);
  const [openBackdrop, setOpenBackdrop] = React.useState(false);
  const open = Boolean(anchorEl);
  const contextMenuId = open ? id : undefined;
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : (event.target as HTMLButtonElement));
    setOpenBackdrop(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setOpenBackdrop(false);
  };

  const popper = (
    <StyledPopper
      id={id}
      open={open}
      className={cx('popper', { additionalMarginTop: isAdditionalMarginTop })}
      anchorEl={anchorEl}
      placement={placement}
      disablePortal
      modifiers={[
        {
          name: 'flip',
          enabled: true,
          options: {
            altBoundary: true,
            rootBoundary: 'document',
            padding: 8,
          },
        },
        {
          name: 'preventOverflow',
          enabled: !isCombinedPieChart,
          options: {
            altAxis: true,
            altBoundary: true,
            tether: true,
            rootBoundary: 'document',
            padding: 8,
          },
        },
        {
          name: 'arrow',
          enabled: true,
          options: {
            element: arrowRef,
          },
        },
      ]}
    >
      <ClickAwayListener onClickAway={handleClose}>
        <div>
          <span
            className="arrow"
            ref={((ref) => setArrowRef(ref)) as LegacyRef<HTMLSpanElement>}
          />
          <Paper>
            {typeof popperContent === 'function'
              ? popperContent(handleClose)
              : popperContent}
          </Paper>
        </div>
      </ClickAwayListener>
    </StyledPopper>
  );

  return (
    <>
      {children({ onClick: handleClick, id: contextMenuId })}
      {hasBackdrop ? (
        <Backdrop sx={{ zIndex: 3 }} open={openBackdrop}>
          {popper}
        </Backdrop>
      ) : (
        <Box
          component="span"
          position="absolute"
          zIndex={1}
          className="popperMenu"
        >
          {popper}
        </Box>
      )}
    </>
  );
};

export default PopperMenu;
